'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { fetchCategories } from '../../services/apiService';
import { pickCategoryName } from '../../utils/categoryUtils';
import {
  FaPills,
  FaLungs,
  FaCapsules,
  FaSprayCan,
  FaHandSparkles,
  FaPalette,
  FaTooth,
  FaHeartbeat,
  FaDumbbell,
  FaMoon,
  FaBandAid,
  FaBaby,
  FaAppleAlt,
  FaUmbrellaBeach,
  FaVial
} from 'react-icons/fa';

// Category mapping configuration
// Map category.en (from API) to custom display name
// Add/remove categories here to control what's displayed
// Check the browser console to see all available categories
const categoryDisplayMap = {
  'PainFever': 'Schmerzen & Fieber',
  'FluColds': 'Grippe & Erkältung',
  'VitaminsSupplements': 'Vitamine',
  'HygieneDisinfection': 'Hygiene & Desinfektion',
  'SkinHairNails': 'Haut, Haare & Nägel',
  'FacialSkinCareMakeUp': 'Gesichtspflege & Make-Up',
  'MouthThroatTeeth': 'Mund, Hals & Zähne',
  'StomachIntestines': 'Magen & Darm',
  'MusclesJoints': 'Muskeln & Gelenke',
  'RelaxationSleepMood': 'Schlaf & Entspannung',
  'FirstAidDressingMat': 'Erste Hilfe',
  'MotherChild': 'Mutter & Kind',
  'Nutrition': 'Ernährung',
  'SunHolidaysTravel': 'Sonne & Reisen',
  'TestsMeasurements': 'Tests & Messungen'
};

// Icon mapping for each category
const categoryIconMap = {
  'PainFever': FaPills,
  'FluColds': FaLungs,
  'VitaminsSupplements': FaCapsules,
  'HygieneDisinfection': FaSprayCan,
  'SkinHairNails': FaHandSparkles,
  'FacialSkinCareMakeUp': FaPalette,
  'MouthThroatTeeth': FaTooth,
  'StomachIntestines': FaHeartbeat,
  'MusclesJoints': FaDumbbell,
  'RelaxationSleepMood': FaMoon,
  'FirstAidDressingMat': FaBandAid,
  'MotherChild': FaBaby,
  'Nutrition': FaAppleAlt,
  'SunHolidaysTravel': FaUmbrellaBeach,
  'TestsMeasurements': FaVial
};

const MoreInfo = React.memo(() => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const fetchCategoriesData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchCategories();
      const allCategories = response.data || [];
      setCategories(allCategories);
      
      // Console log all available categories for selection
      allCategories.forEach((cat, index) => {
      });
    } catch (error) {
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategoriesData();
  }, [fetchCategoriesData]);

  const handleCategoryClick = (category) => {
    const cleanCategoryId = category.category.en.trim();
    sessionStorage.setItem('germanName', category.category.de);
    navigate(`/categories/${cleanCategoryId}/products`);
  };

  // Filter and map categories based on configuration
  const displayCategories = React.useMemo(() => {
    if (Object.keys(categoryDisplayMap).length === 0) {
      // If no mapping configured, show all categories (original behavior)
      return categories.slice(0, 18).map(cat => ({
        ...cat,
        displayName: pickCategoryName(cat.category, language),
        icon: categoryIconMap[cat.category.en] || null
      }));
    }

    // Create a map for quick lookup
    const categoryMap = new Map();
    categories.forEach(cat => {
      categoryMap.set(cat.category.en, cat);
    });

    // Filter and order categories based on categoryDisplayMap order
    return Object.keys(categoryDisplayMap)
      .map(categoryEn => {
        const category = categoryMap.get(categoryEn);
        if (category) {
          return {
            ...category,
            displayName: pickCategoryName(category.category, language),
            icon: categoryIconMap[categoryEn]
          };
        }
        return null;
      })
      .filter(cat => cat !== null);
  }, [categories, categoryDisplayMap, language]);

  return (
    <section 
      className="py-16 px-4 w-full bg-white"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div 
          className="relative text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Decorative elements */}
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-32 h-32 bg-white/5 rounded-full blur-3xl -z-10" aria-hidden="true" />
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-5 leading-tight">
            {t('home.moreInfo.title') || 'Browse Our'}{' '}
            <span className="relative inline-block">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f6d559] via-[#f4c430] to-[#f6d559]">
                {t('home.moreInfo.titleHighlight') || 'Categories'}
              </span>
              <span className="absolute bottom-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#f6d559] to-[#f4c430] rounded-full"></span>
            </span>
          </h2>
          <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto leading-relaxed">
            {t('home.moreInfo.subtitle') || 'Explore our wide range of health and wellness products organized by category'}
          </p>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((i) => (
              <div key={i} className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-gray-200 rounded-2xl animate-pulse"></div>
                <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {displayCategories.map((category, index) => (
              <motion.button
                key={category._id}
                onClick={() => handleCategoryClick(category)}
                className="group flex flex-col items-center justify-center gap-3 text-center transition-all duration-300 bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-2xl p-4 md:p-6 hover:bg-white/20 hover:border-white/40 hover:shadow-xl shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Icon */}
                {category.icon && (
                  <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg" style={{ backgroundColor: '#f6d559' }}>
                    <category.icon className="text-white text-3xl md:text-4xl lg:text-5xl" />
                  </div>
                )}
                
                {/* Category name */}
                <h3 className="text-sm md:text-base font-semibold text-gray-900 group-hover:text-[#f6d559] transition-colors duration-300 line-clamp-2">
                  {category.displayName}
                </h3>
              </motion.button>
            ))}
          </div>
        )}

        {!loading && displayCategories.length > 0 && (
          <div className="text-center mt-8">
            <motion.button
              onClick={() => navigate('/products')}
              className="px-6 py-3 bg-white text-green-600 font-semibold rounded-full hover:bg-green-50 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('home.moreInfo.showAll')}
            </motion.button>
          </div>
        )}
      </div>
    </section>
  );
});

export default MoreInfo;
